import log from "../logger/library";
import { fetch } from "../../fetch/graphql";
import { GRAPHQL_URL, MODE_CAML } from "./constants";
import allContentItemsQuery from "../../fetch/graphql/queries/trending";
import { cacheTrending } from "./cache";

/**
 * Extract card properties from deserialized
 * graphql response
 * @param {object} serialized ostensibly graphql response
 * @return {object} object items property containing array of stories
 */
export const extractCards = (serialized, mode) => {
    return serialized.map((item, idx) => ({
        sourceId: item.sourceId || "",
        title: item.title || "",
        flag: item.flag || "",
        count:
            item.trending && item.trending.numViewers
                ? item.trending.numViewers
                : 0,
        countSrs:
            item.trending && item.trending.numViewersSRS
                ? item.trending.numViewersSRS
                : 0,
        href: item.href || "",
        alt: "",
        rank: idx + 1,
        variant: mode === MODE_CAML ? "SRS" : null,
    }));
};
/**
 * Get query to fetch given section
 * @param {string} section
 * @return {Object} query object
 */
export const buildQuery = (section, pageSize, mode = null) => {
    const trendingPrefix = mode === MODE_CAML ? "/" : "";
    return {
        query: allContentItemsQuery,
        variables: {
            lineupSlug: `trending-${trendingPrefix}${section}`,
            categorySlug: "empty-category",
            pageSize,
        },
    };
};

/**
 * Parse graphql query response
 * Handles akamai caching issue
 * where some query Q generates the same cache key as
 * a batched query of [Q]
 * Resulting in responses for single section, or a batch of one
 * returning either a single data object, or an array of 1 data object
 * @param {Array.<string>} sections
 * @param {Object} response
 * @return {Map.<string, Array.<object>}
 */
export const parseResponse = (
    sections,
    response,
    mode,
    mlDrivenItemsQuery = false
) => {
    if (!Array.isArray(response)) {
        response = [response];
    }

    if (sections.length !== response.length) {
        throw new Error(
            "Result length does not match requested sections length"
        );
    }

    const cardsToExtract = mlDrivenItemsQuery
        ? response.map((resp) => resp.mlDrivenItems.items)
        : response.map((resp) => resp.allContentItems.nodes);

    return cardsToExtract
        .map((cardsList) => extractCards(cardsList, mode))
        .reduce((accum, result, idx) => {
            let section = sections[idx];
            accum[section] = result;
            cacheTrending(section, result, Date.now());
            return accum;
        }, {});
};

/**
 * Fetch content for a single trending section
 * @param {string} section
 * @param {nuber} pageSize
 * @return {Array.<object>}
 */
const fetchOne = (section, pageSize, mode) => {
    let query = buildQuery(section, pageSize, mode);
    return fetch(GRAPHQL_URL, query)
        .then((response) => parseResponse([section], response, mode))
        .then((parsed) => parsed[section]);
};

/**
 * Fetch content for 1-n trending sections
 * @param {Array.<string>} section
 * @param {nuber} pageSize
 * @return {Map.<string, Array.<object>>}
 */
const fetchMany = (sections, pageSize, mode) => {
    let query = sections.map((section) => buildQuery(section, pageSize, mode));
    return fetch(GRAPHQL_URL, query).then((response) =>
        parseResponse(sections, response, mode)
    );
};

/**
 * @param {string|Array.<string>} sections
 * @param {number} pageSize
 * @return {Promise<Array|Map>}
 */
export const fetchTrendingFromGQL = (sections, pageSize, mode = null) => {
    return (
        Array.isArray(sections)
            ? fetchMany(sections, pageSize, mode)
            : fetchOne(sections, pageSize, mode)
    ).catch((error) => {
        log.error(`(Trending) fetch_trending( ${sections} )`, error);
        return Promise.reject(error);
    });
};
